import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import BaseLink from "~/components/links/BaseLink";

import ContentContainer from "~/components/layout/ContentContainer";

import ErrorBoundary from "~/components/ErrorBoundary";

import { breadcrumb } from "~/styles/typographyStyles";
import { mediumSpacing } from "~/styles/helpers";
import { HiddenSectionHeading } from "~/styles/screenReaderText";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import transition from "~/utilities/transition";
import { useStickyContext } from "~/context/StickyContext";

const OuterWrapper = styled.nav`
    padding: 10px 0;
    background-color: ${({ backgroundColor, theme }) =>
        backgroundColor === "white"
            ? theme.palette.white
            : theme.palette.lightGray};
    position: ${({ sticky }) => (sticky ? "sticky" : "static")};
    top: ${({ elementOffset }) =>
        elementOffset ? `${elementOffset}px` : `0px`};
    z-index: ${({ theme }) => theme.zIndex.breadcrumbs};
    ${({ theme, noMarginBottom }) =>
        noMarginBottom ? null : mediumSpacing({ theme })};
`;

const Wrapper = styled.ul`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
`;

const BreadcrumbWrapper = styled.li`
    display: flex;
    align-items: center;

    &:not(:last-of-type)::after {
        ${breadcrumb};
        content: " / ";
        color: ${({ theme }) => theme.colors.typography.links};
        margin: 0 5px;
    }
`;

const Link = styled(BaseLink)`
    ${transition({}, "color")};
    ${breadcrumb};
    color: ${({ theme }) => theme.colors.typography.links};

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.typography.linksHover};
    }
`;

const Last = styled.span`
    ${breadcrumb};
    color: ${({ theme }) => theme.palette.darkBlue};
    font-weight: 900;
`;

const Breadcrumbs = ({
    backgroundColor = "white",
    crumbs = null,
    noMarginBottom = false,
    lastCrumbLabel = "",
    hideBreadcrumbs = false,
    sticky = false,
}) => {
    const { elementOffset, ref } = useStickyContext();

    return (
        !hideBreadcrumbs && (
            <OuterWrapper
                {...{
                    backgroundColor,
                    noMarginBottom,
                    sticky,
                    ref,
                    elementOffset,
                }}
                aria-label="Breadcrumb"
            >
                <HiddenSectionHeading id="breadcrumbHiddenNavLabel">
                    Breadcrumb Navigation
                </HiddenSectionHeading>
                <ContentContainer>
                    <Wrapper aria-labelledby="breadcrumbHiddenNavLabel">
                        <ErrorBoundary>
                            {crumbs &&
                                crumbs.map(({ crumbLabel, pathname }, index) =>
                                    index < crumbs.length - 1 ? (
                                        <BreadcrumbWrapper key={crumbLabel}>
                                            <Link to={pathname}>
                                                {sanitizeAndParseHTML(
                                                    crumbLabel,
                                                )}
                                            </Link>
                                        </BreadcrumbWrapper>
                                    ) : (
                                        <BreadcrumbWrapper
                                            key={
                                                lastCrumbLabel
                                                    ? lastCrumbLabel
                                                    : crumbs[crumbs.length - 1]
                                                          .crumbLabel
                                            }
                                            aria-current="page"
                                        >
                                            <Last>
                                                {sanitizeAndParseHTML(
                                                    lastCrumbLabel
                                                        ? lastCrumbLabel
                                                        : crumbs[
                                                              crumbs.length - 1
                                                          ].crumbLabel,
                                                )}
                                            </Last>
                                        </BreadcrumbWrapper>
                                    ),
                                )}
                        </ErrorBoundary>
                    </Wrapper>
                </ContentContainer>
            </OuterWrapper>
        )
    );
};

Breadcrumbs.propTypes = {
    backgroundColor: PropTypes.oneOf(["white", "lightGray"]),
    crumbs: PropTypes.arrayOf(
        PropTypes.shape({
            crumbLabel: PropTypes.string.isRequired,
            pathname: PropTypes.string,
        }).isRequired,
    ).isRequired,
    hideBreadcrumbs: PropTypes.bool,
    noMarginBottom: PropTypes.bool,
    lastCrumbLabel: PropTypes.string,
    sticky: PropTypes.bool,
};

export default Breadcrumbs;
